import React from 'react';
import Layout from '../../components/layout/Layout';
import PricingCalculator from '../../components/interactive/PricingCalculator';
import { PricingProvider } from '../../context/PricingContext';
import { Link } from 'react-router-dom';

const PricingCalculatorPage: React.FC = () => {
  return (
    <Layout>
      {/* Hero Section - Inspired by Bardeen.ai */}
      <div className="bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50"></div>
        <div className="relative py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
                AI Pricing Calculator
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-600">
                Get accurate cost estimates for your AI projects
              </p>
              <div className="inline-flex items-center bg-blue-50 border border-blue-200 rounded-full px-6 py-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse mr-3"></div>
                <span className="text-blue-700 font-medium">Powered by Advanced Algorithms</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Link */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm shadow-sm">
                <span className="mr-2">💡</span>
                <span>Need help choosing the right service?</span>
                <Link 
                  to="/tools/service-selector" 
                  className="ml-3 text-blue-600 hover:text-blue-800 transition-colors font-medium"
                >
                  Try our AI Service Selector →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Section */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <PricingProvider>
            <PricingCalculator />
          </PricingProvider>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg shadow-sm overflow-hidden">
                  <details className="group">
                    <summary className="cursor-pointer p-6 font-medium text-lg text-gray-800 hover:bg-gray-100 transition-colors focus:outline-none flex items-center justify-between">
                      <span>{faq.question}</span>
                      <div className="ml-4 transform group-open:rotate-180 transition-transform">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </summary>
                    <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const faqData = [
  {
    question: "How accurate is this pricing estimate?",
    answer: "Our pricing calculator uses industry standards and historical project data to provide estimates typically within 10-15% of final costs. For maximum accuracy, we recommend scheduling a free consultation where our specialists can assess your specific requirements."
  },
  {
    question: "What payment options do you offer?",
    answer: "We offer flexible payment structures including monthly retainers, milestone-based payments, and custom payment schedules. For enterprise clients, we can arrange quarterly or annual billing cycles that align with your budget planning."
  },
  {
    question: "Do you offer discounts for non-profits or educational institutions?",
    answer: "Yes, we provide special pricing for non-profit organizations (up to 30% discount), educational institutions (25% off), and early-stage startups. Our AI for Good program also offers pro-bono services for qualifying humanitarian projects."
  },
  {
    question: "What if my project requirements change during development?",
    answer: "We use agile methodologies with built-in flexibility. Minor scope changes (up to 15%) are handled within the project framework. Larger changes go through our transparent change management process, ensuring you maintain full visibility and control."
  },
  {
    question: "Do you provide ongoing support after project completion?",
    answer: "Yes, we offer comprehensive support packages including Basic (monitoring + updates), Professional (optimization + features), and Enterprise (dedicated team + 24/7 support). Think of us as your long-term AI transformation partners."
  }
];

export default PricingCalculatorPage;